import React from 'react';
import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import { Refresh, ErrorOutlineOutlined } from '@mui/icons-material';
import { useRetry } from '@hooks/useRetry';
import { LoadingWrapper } from './LoadingWrapper';
import { useAnnouncement } from '@contexts/AnnouncementContext';
import { fontFamilyMono } from '../../theme/theme';

interface RetryErrorProps {
  error: Error | string;
  onRetry: () => Promise<void>;
  maxAttempts?: number;
  title?: string;
  showDetails?: boolean;
}

export const RetryError: React.FC<RetryErrorProps> = ({
  error,
  onRetry,
  maxAttempts = 3,
  title = 'Failed to load data',
  showDetails = process.env.NODE_ENV === 'development',
}) => {
  const { retry, isRetrying, attempts } = useRetry();
  const { announceError, announce } = useAnnouncement();

  const handleRetry = async () => {
    announce('Retrying...', 'polite');
    await retry(onRetry, {
      maxAttempts,
      delay: 1000,
      backoff: 'exponential',
    });
  };

  const errorMessage = typeof error === 'string' ? error : error.message;

  // Announce error when component mounts
  React.useEffect(() => {
    announceError(`${title}. ${errorMessage}`);
  }, [title, errorMessage, announceError]);

  // Announce when max attempts reached
  React.useEffect(() => {
    if (attempts >= maxAttempts && !isRetrying) {
      announceError(`Failed after ${attempts} attempts. ${errorMessage}`);
    }
  }, [attempts, maxAttempts, isRetrying, errorMessage, announceError]);

  return (
    <LoadingWrapper loading={isRetrying} skeleton="custom" customSkeleton={
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          Retrying... (Attempt {attempts}/{maxAttempts})
        </Typography>
      </Box>
    }>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2} sx={{
          alignItems: 'center'
        }}>
          <ErrorOutlineOutlined sx={{ fontSize: 48, color: 'error.main' }} />

          <Typography variant="h6" sx={{
            color: 'text.primary'
          }}>
            {title}
          </Typography>

          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            {errorMessage}
          </Typography>

          {showDetails && error instanceof Error && error.stack && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                maxWidth: 600,
                maxHeight: 200,
                overflow: 'auto',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: fontFamilyMono,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error.stack}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRetry}
            disabled={isRetrying}
            sx={{ mt: 2 }}
          >
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </Button>

          {attempts > 0 && !isRetrying && (
            <Typography variant="caption" sx={{
              color: 'text.secondary'
            }}>
              Failed after {attempts} attempt{attempts > 1 ? 's' : ''}
            </Typography>
          )}
        </Stack>
      </Paper>
    </LoadingWrapper>
  );
};
